import {
  AlertTriangle,
  Download,
  ExternalLink,
  FileText,
  RefreshCw,
  Upload,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { LoadingButton } from '@/components/loading-button'
import { PdfPreview } from '@/components/pdf-preview'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SearchSelect } from '@/components/search-select'
import { useAuth } from '@/hooks/use-auth'
import { POPdfView } from '@/features/purchase-orders/pdf-view'
import {
  downloadInvoicePurchaseOrderPdf,
  downloadShipmentBookingDocument,
} from '@/features/invoices/data/documents'
import { formatDateOnly, formatMoney, formatStatus } from '@/lib/utils'
import {
  type ApInvoiceAttachment,
  type ApInvoiceDetail,
  type ApInvoicePoLink,
  type ApInvoiceShipmentAllocation,
} from '@/features/invoices/view-models'
import { InvoicePdfDropZone } from '../pdf-drop-zone'
import { formatFileSize } from './formatting'
import { isPdfAttachment, type useInvoiceDocuments } from './use-documents'
import { getApiErrorMessage } from '@/lib/api-error'

const DOCUMENT_TYPE_LABELS = {
  statement: 'Statement',
  credit_memo: 'Credit memo',
  payment_details: 'Payment details',
  other: 'Other document',
} as const

function InvoiceDocumentRow({
  name,
  uploadedAt,
  sizeBytes,
  documentType,
  kind,
  detail,
  available = true,
  onDownload,
}: {
  name: string
  uploadedAt?: string | null
  sizeBytes?: number | null
  documentType?: ApInvoiceAttachment['document_type']
  kind: 'invoice' | 'matched'
  detail?: string | null
  available?: boolean
  onDownload?: () => void
}) {
  return (
    <li className="flex min-w-0 items-center gap-3 py-2">
      <FileText
        className={`size-4 shrink-0 ${kind === 'invoice' ? 'text-red-500' : 'text-blue-600'}`}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm font-medium [overflow-wrap:anywhere]">{name}</p>
        <p className="break-words text-xs text-muted-foreground">
          {detail ||
            [
              documentType && documentType !== 'invoice' ? DOCUMENT_TYPE_LABELS[documentType] : null,
              uploadedAt ? formatDateOnly(uploadedAt.slice(0, 10)) : 'Date pending',
              formatFileSize(sizeBytes),
            ]
              .filter(Boolean)
              .join(' · ')}
        </p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={onDownload}
        disabled={!available || !onDownload}
        aria-label={`Download ${name}`}
      >
        <Download className="size-3.5" />
      </Button>
    </li>
  )
}

/** Both documents share one page height on desktop so lines can be read across. */
const COMPARE_VIEWPORT = 'max-h-[70dvh] lg:max-h-[min(80dvh,880px)]'

function ComparePaneHeader({
  title,
  detail,
  onDownload,
  downloadLabel,
  switcher,
  children,
}: {
  title: string
  detail?: React.ReactNode
  onDownload?: () => void
  downloadLabel: string
  /** Sits beside the title; on phones it takes its own row so every choice stays visible. */
  switcher?: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <div className="flex min-h-9 min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <h2 className="shrink-0 text-base font-semibold">{title}</h2>
        {detail && <div className="min-w-0 truncate text-sm text-muted-foreground">{detail}</div>}
        {switcher && <div className="min-w-0 max-sm:hidden">{switcher}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {children}
        {onDownload && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onDownload}
            aria-label={downloadLabel}
            title={downloadLabel}
          >
            <Download className="size-4" />
          </Button>
        )}
      </div>
      {switcher && <div className="w-full sm:hidden">{switcher}</div>}
    </div>
  )
}

function ComparePaneMessage({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex h-[360px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
      {icon}
      {children}
    </div>
  )
}

function InvoicePane({
  invoice,
  documents,
}: {
  invoice: ApInvoiceDetail
  documents: ReturnType<typeof useInvoiceDocuments>
}) {
  const { primaryPdf, primaryPdfUrl, primaryPdfLoading, primaryPdfError, downloadAttachment } =
    documents
  const name = primaryPdf?.file_name || invoice.invoice_number || 'Invoice PDF'
  const isPdf = primaryPdf ? isPdfAttachment(primaryPdf) : false
  return (
    <section aria-label="Invoice document" className="min-w-0 space-y-2">
      <ComparePaneHeader
        title="Invoice"
        detail={primaryPdf?.file_name}
        downloadLabel={`Download ${name}`}
        onDownload={
          primaryPdf ? () => downloadAttachment(primaryPdf.id, primaryPdf.file_name) : undefined
        }
      />
      {primaryPdfUrl ? (
        isPdf ? (
          <PdfPreview
            key={primaryPdfUrl}
            name={name}
            url={primaryPdfUrl}
            viewportClassName={COMPARE_VIEWPORT}
          />
        ) : (
          <iframe
            title={`${name} preview`}
            src={primaryPdfUrl}
            className="h-[460px] w-full rounded-md border bg-muted lg:h-[min(80dvh,880px)]"
          />
        )
      ) : primaryPdfLoading ? (
        <ComparePaneMessage icon={<RefreshCw className="size-6 animate-spin" />}>
          Loading invoice…
        </ComparePaneMessage>
      ) : primaryPdfError ? (
        <ComparePaneMessage icon={<AlertTriangle className="size-6" />}>
          <p className="font-medium text-foreground">The invoice could not be loaded</p>
          <p>{primaryPdfError}</p>
        </ComparePaneMessage>
      ) : (
        <ComparePaneMessage icon={<FileText className="size-7" />}>
          No invoice PDF yet. Upload one under PDFs and attachments.
        </ComparePaneMessage>
      )}
    </section>
  )
}

/** The POs this invoice bills, once each, in link order. */
function linkedPurchaseOrders(invoice: ApInvoiceDetail) {
  const byUuid = new Map<string, NonNullable<ApInvoicePoLink['purchase_order']>>()
  for (const link of invoice.po_links ?? []) {
    const po = link.purchase_order
    if (po?.uuid && !byUuid.has(po.uuid)) byUuid.set(po.uuid, po)
  }
  return [...byUuid.values()]
}

function PurchaseOrderPane({ invoice }: { invoice: ApInvoiceDetail }) {
  const { getAccessToken } = useAuth()
  const purchaseOrders = linkedPurchaseOrders(invoice)
  const [selectedUuid, setSelectedUuid] = useState<string | undefined>()
  const selected =
    purchaseOrders.find((po) => po.uuid === selectedUuid) ?? purchaseOrders[0] ?? null
  const fileName = selected?.pdf_filename || `PO-${selected?.po_number}.pdf`

  const download = async () => {
    if (!selected) return
    try {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      await downloadInvoicePurchaseOrderPdf(token, selected.uuid, fileName)
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'PO download failed'))
    }
  }

  return (
    <section aria-label="Purchase order document" className="min-w-0 space-y-2">
      <ComparePaneHeader
        title="Purchase order"
        switcher={
          purchaseOrders.length > 1 && selected ? (
            <Tabs value={selected.uuid} onValueChange={setSelectedUuid} className="min-w-0">
              <TabsList
                aria-label="Linked purchase orders"
                className="h-8 max-w-full justify-start overflow-x-auto"
              >
                {purchaseOrders.map((po) => (
                  <TabsTrigger key={po.uuid} value={po.uuid} className="flex-none px-2.5 text-xs tabular-nums">
                    {po.po_number}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          ) : undefined
        }
        detail={
          purchaseOrders.length === 1 && selected ? (
            <Link
              to="/purchase-orders/$poId"
              params={{ poId: selected.uuid }}
              className="text-primary hover:underline"
            >
              PO #{selected.po_number}
            </Link>
          ) : undefined
        }
        downloadLabel={`Download PO #${selected?.po_number ?? ''}`}
        onDownload={selected?.pdf_filename ? () => void download() : undefined}
      >
        {selected && purchaseOrders.length > 1 && (
          <Button asChild variant="ghost" size="icon">
            <Link
              to="/purchase-orders/$poId"
              params={{ poId: selected.uuid }}
              aria-label={`Open PO #${selected.po_number}`}
              title={`Open PO #${selected.po_number}`}
            >
              <ExternalLink className="size-4" />
            </Link>
          </Button>
        )}
      </ComparePaneHeader>
      {!selected ? (
        <ComparePaneMessage icon={<FileText className="size-7" />}>
          No PO linked yet. Link one in the invoice summary to compare it here.
        </ComparePaneMessage>
      ) : selected.pdf_filename ? (
        <POPdfView
          key={selected.uuid}
          poUuid={selected.uuid}
          poNumber={selected.po_number}
          className="min-w-0"
          viewportClassName={COMPARE_VIEWPORT}
        />
      ) : (
        <ComparePaneMessage icon={<FileText className="size-7" />}>
          PO #{selected.po_number} has no PDF yet.
        </ComparePaneMessage>
      )}
    </section>
  )
}

/** The shipments a freight invoice bills, once each, with what we booked for them. */
function bookedShipments(invoice: ApInvoiceDetail) {
  const byUuid = new Map<string, ApInvoiceShipmentAllocation>()
  for (const allocation of invoice.shipment_allocations ?? []) {
    if (!byUuid.has(allocation.shipment_uuid_snapshot))
      byUuid.set(allocation.shipment_uuid_snapshot, allocation)
  }
  return [...byUuid.values()]
}

/** A shipment's booking document, loaded the way the invoice PDF is (see use-documents). */
function useShipmentBookingDocument(shipmentUuid: string | undefined) {
  const { getAccessToken } = useAuth()
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState<{
    status: 'idle' | 'loading' | 'ready' | 'missing' | 'error'
    kind?: 'bol' | 'label'
    url?: string
  }>({ status: 'idle' })

  useEffect(() => {
    if (!shipmentUuid) return
    let active = true
    let objectUrl: string | null = null
    setState({ status: 'loading' })
    void (async () => {
      try {
        const token = await getAccessToken()
        if (!token) throw new Error('Failed to acquire access token')
        const document = await downloadShipmentBookingDocument(token, shipmentUuid)
        if (!active) return
        if (!document) return setState({ status: 'missing' })
        objectUrl = URL.createObjectURL(document.blob)
        setState({ status: 'ready', kind: document.kind, url: objectUrl })
      } catch {
        if (active) setState({ status: 'error' })
      }
    })()
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [getAccessToken, shipmentUuid, attempt])

  return { ...state, retry: () => setAttempt((value) => value + 1) }
}

function BookingPane({ invoice }: { invoice: ApInvoiceDetail }) {
  const shipments = bookedShipments(invoice)
  const [selectedUuid, setSelectedUuid] = useState<string | undefined>()
  const selected =
    shipments.find((shipment) => shipment.shipment_uuid_snapshot === selectedUuid) ?? shipments[0]
  const document = useShipmentBookingDocument(selected?.shipment_uuid_snapshot)
  const booked = selected?.expected_payable
  const label = (shipment: ApInvoiceShipmentAllocation) =>
    shipment.po_number_snapshot || shipment.project_name_snapshot || 'Shipment'

  return (
    <section aria-label="Booked shipment document" className="min-w-0 space-y-2">
      <ComparePaneHeader
        title="Booked"
        detail={
          selected && shipments.length === 1 ? (
            selected.purchase_order_uuid_snapshot ? (
              <Link
                to="/purchase-orders/$poId"
                params={{ poId: selected.purchase_order_uuid_snapshot }}
                className="text-primary hover:underline"
              >
                PO #{label(selected)}
              </Link>
            ) : (
              label(selected)
            )
          ) : undefined
        }
        switcher={
          shipments.length > 1 && selected ? (
            <SearchSelect
              ariaLabel="Shipment on this invoice"
              className="h-8 w-full sm:w-64"
              value={selected.shipment_uuid_snapshot}
              options={shipments.map((shipment) => ({
                value: shipment.shipment_uuid_snapshot,
                label: label(shipment),
                description:
                  shipment.expected_payable?.amount != null
                    ? `Booked ${formatMoney(shipment.expected_payable.amount)}`
                    : null,
              }))}
              onChange={(value) => value && setSelectedUuid(value)}
              searchPlaceholder="Search PO numbers"
            />
          ) : undefined
        }
        downloadLabel={`Download booking for ${selected ? label(selected) : 'shipment'}`}
        onDownload={
          document.url && selected
            ? () => {
                const anchor = window.document.createElement('a')
                anchor.href = document.url!
                anchor.download = `${document.kind === 'bol' ? 'BOL' : 'Label'}-${label(selected)}.pdf`
                anchor.click()
              }
            : undefined
        }
      />
      {booked && (
        <p className="text-sm text-muted-foreground">
          Booked{' '}
          <span className="font-semibold text-foreground tabular-nums">
            {formatMoney(booked.amount)}
          </span>
          {booked.carrier_name_snapshot && ` with ${booked.carrier_name_snapshot}`}
          {booked.service_level_snapshot && ` · ${booked.service_level_snapshot}`}
        </p>
      )}
      {!selected ? null : document.status === 'ready' && document.url ? (
        <PdfPreview
          key={document.url}
          name={`${document.kind === 'bol' ? 'BOL' : 'Label'} for ${label(selected)}`}
          url={document.url}
          viewportClassName={COMPARE_VIEWPORT}
        />
      ) : document.status === 'error' ? (
        <ComparePaneMessage icon={<AlertTriangle className="size-6" />}>
          <p className="font-medium text-foreground">The booking document could not be loaded</p>
          <Button size="sm" variant="outline" onClick={document.retry}>
            Try again
          </Button>
        </ComparePaneMessage>
      ) : document.status === 'missing' ? (
        <ComparePaneMessage icon={<FileText className="size-7" />}>
          No BOL or label is stored for this shipment.
        </ComparePaneMessage>
      ) : (
        <ComparePaneMessage icon={<RefreshCw className="size-6 animate-spin" />}>
          Loading booking…
        </ComparePaneMessage>
      )}
    </section>
  )
}

/** What we booked on the left, what was billed on the right, so one can be checked against the other (USA-1178). */
export function InvoicePoComparison({
  invoice,
  documents,
}: {
  invoice: ApInvoiceDetail
  documents: ReturnType<typeof useInvoiceDocuments>
}) {
  // Freight invoices bill shipments we booked; supplier invoices bill the PO we sent.
  const freight = (invoice.shipment_allocations ?? []).length > 0
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {freight ? <BookingPane invoice={invoice} /> : <PurchaseOrderPane invoice={invoice} />}
      <InvoicePane invoice={invoice} documents={documents} />
    </div>
  )
}

export function InvoiceDocuments({
  invoice,
  documents,
}: {
  invoice: ApInvoiceDetail
  documents: ReturnType<typeof useInvoiceDocuments>
}) {
  const {
    matchedDocumentsQuery,
    uploadPdf,
    file,
    setFile,
    correctedInvoiceUpload,
    setCorrectedInvoiceUpload,
    invoiceDocuments,
    matchedDocuments,
    downloadMatchedDocument,
    submitPdfUpload,
    downloadAttachment,
  } = documents
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <InvoicePdfDropZone inputId="ap-invoice-upload" file={file} onFileChange={setFile} />
        {invoice.status === 'wait_for_adjustment' && (
          <div className="flex items-center justify-between gap-4 py-1">
            <Label htmlFor="corrected-invoice-upload" className="text-sm">
              Corrected invoice
            </Label>
            <Switch
              id="corrected-invoice-upload"
              checked={correctedInvoiceUpload}
              onCheckedChange={setCorrectedInvoiceUpload}
            />
          </div>
        )}
        {file && (
          <LoadingButton
            size="sm"
            loading={uploadPdf.isPending}
            loadingText="Uploading..."
            onClick={submitPdfUpload}
          >
            <Upload className="h-3.5 w-3.5" /> Upload PDF
          </LoadingButton>
        )}
      </div>
      <section aria-label="Invoice files">
        <h3 className="text-xs font-medium text-muted-foreground">Invoice files</h3>
        {invoiceDocuments.length ? (
          <ul className="divide-y">
            {invoiceDocuments.map((attachment) => (
              <InvoiceDocumentRow
                key={attachment.id}
                name={attachment.file_name}
                uploadedAt={attachment.created_at || attachment.uploaded_at}
                sizeBytes={attachment.size_bytes}
                documentType={attachment.document_type}
                kind="invoice"
                onDownload={() => downloadAttachment(attachment.id, attachment.file_name)}
              />
            ))}
          </ul>
        ) : (
          <p className="py-2 text-sm text-muted-foreground">No invoice files yet.</p>
        )}
      </section>
      <section aria-label="PO and approval documents">
        <h3 className="text-xs font-medium text-muted-foreground">PO and approval documents</h3>
        {matchedDocumentsQuery.isLoading ? (
          <p className="py-2 text-sm text-muted-foreground">Loading…</p>
        ) : matchedDocuments.length ? (
          <ul className="divide-y">
            {matchedDocuments.map((document) => (
              <InvoiceDocumentRow
                key={document.key}
                name={document.name}
                kind="matched"
                uploadedAt={document.captured_at}
                available={document.available}
                detail={
                  document.available
                    ? formatStatus(document.kind)
                    : document.missing_reason || 'Not available'
                }
                onDownload={
                  document.available
                    ? () => void downloadMatchedDocument(document.download_path, document.name)
                    : undefined
                }
              />
            ))}
          </ul>
        ) : (
          <p className="py-2 text-sm text-muted-foreground">None yet.</p>
        )}
      </section>
    </div>
  )
}
